(() => {
  const ROOT_SELECTOR = '[data-cbh-native-finder]';
  const FEED_CACHE = new Map();
  const PHASES = [
    { label: 'Start', description: 'Choose how to begin' },
    { label: 'Choose a format', description: 'Select a CBD product format' },
    { label: 'Refine choices', description: 'Compare available product details' },
    { label: 'View matches', description: 'Review current products' },
  ];

  const escapeHtml = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const asText = (value) => {
    if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(' | ');
    if (value && typeof value === 'object') {
      if (typeof value.value !== 'undefined') return asText(value.value);
      return Object.values(value).map(asText).filter(Boolean).join(' | ');
    }
    return value == null ? '' : String(value).trim();
  };

  const normalise = (value) =>
    asText(value)
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const unique = (values) => [...new Set(values.filter(Boolean))];
  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const safeUrl = (value, fallback = '#') => {
    const candidate = String(value || '').trim();
    if (!candidate) return fallback;
    if (candidate.startsWith('/') || candidate.startsWith('#')) return candidate;
    try {
      const parsed = new URL(candidate, window.location.origin);
      if (parsed.origin === window.location.origin || /^https?:$/.test(parsed.protocol)) return parsed.href;
    } catch (error) {
      return fallback;
    }
    return fallback;
  };

  const formatLabels = {
    oils: 'CBD oils',
    gummies: 'CBD gummies',
    capsules: 'CBD capsules & tablets',
    topicals: 'CBD topicals & skincare',
    drinks: 'CBD drinks',
    patches: 'CBD patches',
    vape: 'CBD vape products',
    extracts: 'CBD extracts & concentrates',
    not_sure: 'Not sure yet',
    still_comparing: 'Still comparing formats',
  };

  const typeLabels = {
    any: 'No CBD type preference',
    full: 'Full Spectrum',
    broad: 'Broad Spectrum',
    isolate: 'CBD Isolate',
  };

  const flavourLabels = {
    any: 'No flavour preference',
    natural: 'Natural or unflavoured',
    fruit: 'Fruit or sweet flavours',
    mint: 'Mint or cooling flavours',
    dessert: 'Dessert or drink-style flavours',
  };

  const shoppingLabels = {
    any: 'No extra preference',
    offers: 'Current offers',
    bundles: 'Bundles or multipacks',
  };

  const browseLabels = {
    oils: 'oils & tinctures',
    gummies: 'gummies',
    capsules: 'capsules & tablets',
    topicals: 'topicals & skincare',
    drinks: 'drinks',
    patches: 'patches',
    vape: 'vape products',
    extracts: 'extracts & concentrates',
  };

  const iconPaths = {
    compass: '<circle cx="12" cy="12" r="8.5"/><path d="m15.5 8.5-2.2 4.8-4.8 2.2 2.2-4.8 4.8-2.2Z"/>',
    direct: '<path d="M5 5h8v5H5zM11 14h8v5h-8z"/><path d="M13 7.5h3a2 2 0 0 1 2 2V14M9 16.5H6a2 2 0 0 1-2-2V10"/>',
    compare: '<path d="M5 6h12M5 12h14M5 18h9"/><circle cx="19" cy="6" r="1.5"/><circle cx="3" cy="12" r="1.5"/><circle cx="16" cy="18" r="1.5"/>',
    drop: '<path d="M12 3.5s-5 5.5-5 10a5 5 0 0 0 10 0c0-4.5-5-10-5-10Z"/><path d="M9.7 14.2a2.5 2.5 0 0 0 2.4 1.8"/>',
    gummy: '<rect x="5" y="6" width="14" height="12" rx="5"/><path d="M9 6V4m6 2V4M8.5 12h.01m7 0h.01"/>',
    capsule: '<path d="m8.2 16.8 8.6-8.6a3.5 3.5 0 0 0-5-5l-8.6 8.6a3.5 3.5 0 1 0 5 5Z"/><path d="m8 8 8 8"/>',
    topical: '<path d="M8 7h8l1 13H7L8 7Z"/><path d="M10 7V4h4v3m-4 6h4"/>',
    drink: '<path d="M6 4h12l-1.2 16H7.2L6 4Z"/><path d="M8 9h8m-3-5 3-2"/>',
    patch: '<rect x="4" y="4" width="16" height="16" rx="4"/><path d="M9 12h6m-3-3v6"/>',
    vape: '<path d="M5 14h12v5H5zM8 14V7h6v7m-4-7V4h2v3m5 9h2"/>',
    extract: '<path d="m12 3 8 7-8 11-8-11 8-7Z"/><path d="m4 10 8 3 8-3M8 6.5 12 13l4-6.5"/>',
    question: '<circle cx="12" cy="12" r="9"/><path d="M9.8 9a2.4 2.4 0 1 1 3.7 2c-1 .6-1.5 1.1-1.5 2.2M12 17h.01"/>',
    eliquid: '<path d="M9 6h6l1 14H8L9 6Z"/><path d="M10 6V3h4v3m-3 7h2"/>',
    pod: '<rect x="7" y="4" width="10" height="16" rx="3"/><path d="M10 4V2h4v2m-4 11h4"/>',
    device: '<rect x="8" y="3" width="8" height="18" rx="3"/><path d="M10.5 7h3M12 17h.01"/>',
    layers: '<path d="m12 3 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5m-18 4 9 5 9-5"/>',
    leaf: '<path d="M19 4C10 4 5 8 5 14a5 5 0 0 0 5 5c6 0 9-6 9-15Z"/><path d="M5 20c2-5 5-8 10-11"/>',
    fruit: '<path d="M12 7c-5-3-9 1-7 7 1.5 5 5 7 7 7s5.5-2 7-7c2-6-2-10-7-7Z"/><path d="M12 7c0-2 1-4 4-5m-4 5c-2-2-4-2-6-1"/>',
    snow: '<path d="M12 2v20M4.2 6.5l15.6 11M4.2 17.5l15.6-11M9 4.5l3 2 3-2M9 19.5l3-2 3 2"/>',
    cup: '<path d="M5 8h12v7a5 5 0 0 1-5 5H10a5 5 0 0 1-5-5V8Z"/><path d="M17 10h1a3 3 0 0 1 0 6h-1M9 5c0-1 1-1.5 1-2.5m4 2.5c0-1 1-1.5 1-2.5"/>',
    sparkle: '<path d="m12 3 1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3Z"/><path d="m19 16 .6 2.4L22 19l-2.4.6L19 22l-.6-2.4L16 19l2.4-.6L19 16Z"/>',
    sale: '<path d="M4 5v6l8 8 7-7-8-8H5a1 1 0 0 0-1 1Z"/><circle cx="8" cy="8" r="1"/><path d="m10 14 4-4m-4 0h.01m4 4h.01"/>',
    bundle: '<rect x="4" y="7" width="16" height="13" rx="2"/><path d="M12 7v13M4 11h16M8 7c-2 0-3-1-3-2.2C5 3.5 6 3 7 3c2 0 5 4 5 4m4-4c2 0 3 1 3 2.2C19 6.5 18 7 17 7h-5s2-4 4-4Z"/>',
    adult: '<circle cx="12" cy="12" r="9"/><path d="M8 9h2v6M8 15h4m3-6h2.5L15 15h3"/>',
    check: '<circle cx="12" cy="12" r="9"/><path d="m8 12 2.5 2.5L16 9"/>',
  };

  const iconMarkup = (name) => {
    const paths = iconPaths[name] || iconPaths.question;
    return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;
  };

  class FinderFeedError extends Error {
    constructor(code, message, status = 0) {
      super(message);
      this.name = 'FinderFeedError';
      this.code = code;
      this.status = status;
    }
  }

  class CBHNativeFinder {
    constructor(root) {
      this.root = root;
      this.config = this.readJson(root.querySelector('[data-cbh-finder-config]'));
      this.tool = root.querySelector('[data-cbh-finder-tool]');
      this.form = root.querySelector('[data-cbh-finder-form]');
      this.stage = root.querySelector('[data-cbh-finder-stage]');
      this.results = root.querySelector('[data-cbh-results]');
      this.live = root.querySelector('[data-cbh-finder-live]');
      this.error = root.querySelector('[data-cbh-finder-error]');
      this.feedStatus = root.querySelector('[data-cbh-feed-status]');
      this.controls = root.querySelector('[data-cbh-controls]');
      this.backButton = root.querySelector('[data-cbh-back]');
      this.continueButton = root.querySelector('[data-cbh-continue]');
      this.progressLabel = root.querySelector('[data-cbh-progress-label]');
      this.progressCount = root.querySelector('[data-cbh-progress-count]');
      this.progressBar = root.querySelector('[data-cbh-progress-bar]');
      this.stepList = root.querySelector('[data-cbh-step-list]');
      this.answerSummary = root.querySelector('[data-cbh-answer-summary]');
      this.answers = {};
      this.stepIndex = 0;
      this.currentFeed = null;
      this.pendingCatalogue = null;
      this.pendingResultRoute = null;
      this.initialised = false;
      this.analyticsState = {
        started: false,
        resultViewed: false,
        abandonmentPublished: false,
        stepCompletions: new Map(),
      };
      this.pageHideHandler = () => this.trackAbandonment();
      this.sectionUnloadHandler = (event) => {
        if (event.target === this.root || event.target?.contains?.(this.root)) this.destroy();
      };
      this.destroyed = false;
    }

    init() {
      if (this.initialised || !this.config || !this.form || !this.stage) return;
      this.initialised = true;

      this.form.addEventListener('submit', (event) => this.onContinue(event));
      this.backButton?.addEventListener('click', () => this.onBack());
      this.root.addEventListener('click', (event) => this.onRootClick(event));
      this.root.addEventListener('product-form:state', (event) => this.onProductFormState(event));
      window.addEventListener('pagehide', this.pageHideHandler, { capture: true });
      document.addEventListener('shopify:section:unload', this.sectionUnloadHandler);
      this.renderStep(false);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      window.removeEventListener('pagehide', this.pageHideHandler, true);
      document.removeEventListener('shopify:section:unload', this.sectionUnloadHandler);
    }

    readJson(element) {
      if (!element) return null;
      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.error('CBD Product Finder configuration could not be read.', error);
        return null;
      }
    }

    pathFromUrl(value) {
      try {
        return new URL(String(value || ''), window.location.origin).pathname.replace(/\/$/, '') || '/';
      } catch (error) {
        return '';
      }
    }

    isExpectedGuide(value, pathname) {
      return this.pathFromUrl(value) === pathname.replace(/\/$/, '');
    }

    publishAnalytics(name, detail = {}) {
      const eventName = `the_cbd_hut:product_finder_${name}`;
      const payload = {
        finder_name: 'cbd_product_finder',
        finder_version: this.config.analyticsVersion || 'aud3d',
        ...detail,
      };

      try {
        if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(eventName, payload);
      } catch (error) {
        // Analytics must never interrupt the finder journey.
      }

      try {
        if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: eventName, ...payload });
      } catch (error) {
        // Keep third-party analytics optional and non-blocking.
      }

      try {
        this.root.dispatchEvent(new CustomEvent('cbh:product-finder-event', { detail: { name: eventName, ...payload } }));
      } catch (error) {
        // The native event is an integration hook only.
      }
    }

    trackStart(source = 'tool_interaction') {
      if (this.analyticsState.started) return;
      this.analyticsState.started = true;
      this.publishAnalytics('started', { source });
    }

    trackStepCompletion(step) {
      if (!step) return;
      const completionNumber = (this.analyticsState.stepCompletions.get(step.key) || 0) + 1;
      this.analyticsState.stepCompletions.set(step.key, completionNumber);
      this.publishAnalytics('step_completed', {
        step_key: step.key,
        phase: Number(step.phase || 1),
        completion_number: completionNumber,
      });
    }

    trackResultView(resultType, detail = {}) {
      this.analyticsState.resultViewed = true;
      this.publishAnalytics('results_viewed', { result_type: resultType, ...detail });
    }

    trackResultClick(anchor) {
      if (!anchor) return;
      const productCard = anchor.closest('.cbh-native-finder__product-card');
      const destinationPath = this.pathFromUrl(anchor.getAttribute('href'));
      let destinationType = 'result_route';
      if (productCard) destinationType = 'product';
      else if (anchor.closest('.cbh-native-finder__result-support')) destinationType = 'support_guide';
      else if (anchor.closest('.cbh-native-finder__route-grid')) destinationType = 'direct_format_route';
      else if (anchor.closest('.cbh-native-finder__boundary-links')) destinationType = 'finder_support';

      const payload = { destination_type: destinationType, destination_path: destinationPath };
      if (productCard) {
        payload.product_id = productCard.dataset.cbhProductId || '';
        payload.product_handle = productCard.dataset.cbhProductHandle || '';
        payload.match_type = productCard.dataset.cbhMatchKind || '';
        payload.position = Number(productCard.dataset.cbhResultPosition || 0);
      }
      this.publishAnalytics(destinationType === 'product' || anchor.closest('[data-cbh-results]') ? 'result_clicked' : 'support_clicked', payload);
    }

    onProductFormState(event) {
      if (!event.detail?.submitted || event.detail.success !== true) return;
      const productCard = event.target.closest('.cbh-native-finder__product-card');
      if (!productCard) return;
      this.publishAnalytics('product_added_to_cart', {
        product_id: productCard.dataset.cbhProductId || '',
        product_handle: productCard.dataset.cbhProductHandle || '',
        variant_id: String(event.detail.variantId || ''),
        match_type: productCard.dataset.cbhMatchKind || '',
        position: Number(productCard.dataset.cbhResultPosition || 0),
      });
    }

    trackAbandonment() {
      if (!this.analyticsState.started || this.analyticsState.resultViewed || this.analyticsState.abandonmentPublished) return;
      this.analyticsState.abandonmentPublished = true;
      const step = this.activeSteps()[this.stepIndex];
      this.publishAnalytics('abandoned', {
        step_key: step?.key || 'unknown',
        phase: Number(step?.phase || 1),
      });
    }

    definitions() {
      const allCbdUrl = safeUrl(this.config.guides?.allCbd, '/collections/all-cbd-products');
      const compareTypesUrl = safeUrl(this.config.guides?.compareTypes, allCbdUrl);
      const formatGuideUrl = safeUrl(this.config.guides?.format, allCbdUrl);
      const compareTypesAvailable = this.isExpectedGuide(compareTypesUrl, '/pages/compare-cbd-types');
      const formatGuideAvailable = this.isExpectedGuide(formatGuideUrl, '/pages/shop-by-format');
      const compareTypesLabel = compareTypesAvailable ? 'CBD type guide' : 'current CBD range';
      const formatGuideLabel = formatGuideAvailable ? 'Compare every format' : 'Browse all CBD products';

      return {
        approach: {
          key: 'approach',
          phase: 1,
          kicker: 'Start here',
          signal: 'Choose your route',
          title: 'How would you like to begin?',
          help: 'Choose a guided comparison or go straight from a format to current products. This answer never changes product ranking.',
          options: [
            { value: 'guided', icon: 'compass', label: 'Guide me step by step', text: 'Refine CBD type, flavour and shopping preferences where available.' },
            { value: 'direct', icon: 'direct', label: 'I know the format I want', text: 'Choose a format and view current products straight away.' },
            {
              value: 'compare',
              icon: 'compare',
              label: 'Compare CBD types first',
              text: compareTypesAvailable ? 'Open the Full, Broad and Isolate guide.' : 'Review the current range without assuming a CBD type.',
            },
          ],
        },
        format: {
          key: 'format',
          phase: 2,
          kicker: 'Choose a format',
          signal: this.answers.approach === 'direct' ? 'Quick format route' : 'Sets your product range',
          title: 'Which CBD format would you like to explore?',
          help: this.answers.approach === 'direct'
            ? `Choose a format to view current available products. CBD vape products retain the necessary subtype and adult access checks. <a href="${escapeHtml(formatGuideUrl)}">${escapeHtml(formatGuideLabel)}</a>.`
            : `Choose how you prefer the product to be supplied. We will then show only relevant preferences found in the current range. <a href="${escapeHtml(formatGuideUrl)}">${escapeHtml(formatGuideLabel)}</a>.`,
          className: 'format',
          options: [
            { value: 'oils', icon: 'drop', label: 'CBD Oils', text: 'Bottle and dropper formats.' },
            { value: 'gummies', icon: 'gummy', label: 'CBD Gummies', text: 'Flavour-led measured pieces.' },
            { value: 'capsules', icon: 'capsule', label: 'Capsules & Tablets', text: 'Measured swallowable units.' },
            { value: 'topicals', icon: 'topical', label: 'Topicals & Skincare', text: 'External-use body care.' },
            { value: 'drinks', icon: 'drink', label: 'CBD Drinks', text: 'Ready-made drink formats.' },
            { value: 'patches', icon: 'patch', label: 'CBD Patches', text: 'External patch formats.' },
            { value: 'vape', icon: 'vape', label: 'CBD Vape Products', text: 'E-liquids, pods and devices.' },
            { value: 'extracts', icon: 'extract', label: 'Extracts & Concentrates', text: 'Specialist CBD formats.' },
            { value: 'not_sure', icon: 'question', label: 'I am not sure', text: 'Narrow the formats with one more question.' },
          ],
        },
        routePreference: {
          key: 'routePreference',
          phase: 2,
          kicker: 'Choose a format',
          signal: 'One practical follow-up',
          title: 'Which product style sounds most familiar?',
          help: 'Choose only by how the product is supplied. We will not ask about health conditions or desired effects.',
          options: [
            { value: 'oils', icon: 'drop', label: 'A bottle and dropper', text: 'Explore CBD oils and tinctures.' },
            { value: 'gummies', icon: 'gummy', label: 'Measured edible pieces', text: 'Explore CBD gummies.' },
            { value: 'capsules', icon: 'capsule', label: 'Capsules or tablets', text: 'Explore measured swallowable units.' },
            { value: 'topicals', icon: 'topical', label: 'External-use body care', text: 'Explore balms, gels, creams and skincare.' },
            { value: 'drinks', icon: 'drink', label: 'A ready-made drink', text: 'Explore CBD drink formats.' },
            { value: 'still_comparing', icon: 'compare', label: 'I still want to compare', text: 'Open the complete format guide first.' },
          ],
        },
        vapeSubtype: {
          key: 'vapeSubtype',
          phase: 3,
          kicker: 'Refine your choices',
          signal: 'Available vape formats',
          title: 'Which CBD vape format are you browsing?',
          help: 'Keeping e-liquids, pods and hardware separate prevents a liquid choice from returning a device.',
          options: [
            { value: 'eliquid', icon: 'eliquid', label: 'CBD E-Liquids', text: 'Bottled e-liquid formats.' },
            { value: 'pods', icon: 'pod', label: 'Pods & Cartridges', text: 'Prefilled pod and cartridge formats.' },
            { value: 'devices', icon: 'device', label: 'Devices & Accessories', text: 'Hardware, batteries and related accessories.' },
            { value: 'any', icon: 'layers', label: 'Show all vape formats', text: 'Compare the available vape range.' },
          ],
        },
        age: {
          key: 'age',
          phase: 3,
          kicker: 'Adult access check',
          signal: 'CBD vape products',
          title: 'Can you confirm you are aged 18 or over?',
          help: 'The CBD vape route is available only to adults aged 18+. This answer does not change product ranking.',
          options: [
            { value: 'yes', icon: 'adult', label: 'Yes, I am 18 or over', text: 'Continue to the available vape products.' },
            { value: 'no', icon: 'question', label: 'No', text: 'Choose another CBD format instead.' },
          ],
        },
        cbdType: {
          key: 'cbdType',
          phase: 3,
          kicker: 'Refine your choices',
          signal: 'Available CBD types',
          title: 'Do you have a CBD type preference?',
          help: `Only CBD types currently available in this format can be selected. If you are comparing the terms, open the <a href="${escapeHtml(compareTypesUrl)}">${escapeHtml(compareTypesLabel)}</a>.`,
          options: [
            { value: 'any', icon: 'layers', label: 'No CBD type preference', text: 'Keep every available CBD profile.' },
            { value: 'full', icon: 'leaf', label: 'Full Spectrum', text: 'Show Full Spectrum products only.' },
            { value: 'broad', icon: 'compare', label: 'Broad Spectrum', text: 'Show Broad Spectrum products only.' },
            { value: 'isolate', icon: 'extract', label: 'CBD Isolate', text: 'Show isolate products only.' },
          ],
        },
        flavour: {
          key: 'flavour',
          phase: 3,
          kicker: 'Refine your choices',
          signal: 'Available flavours',
          title: 'Do you have a flavour preference?',
          help: 'Only flavour families found in the available products are shown. You can keep the full range open.',
          options: [
            { value: 'any', icon: 'layers', label: 'No flavour preference', text: 'Keep every available flavour.' },
            { value: 'natural', icon: 'leaf', label: 'Natural or unflavoured', text: 'Natural, neutral or unflavoured listings.' },
            { value: 'fruit', icon: 'fruit', label: 'Fruit or sweet flavours', text: 'Fruit-led and sweet flavour listings.' },
            { value: 'mint', icon: 'snow', label: 'Mint or cooling flavours', text: 'Mint, menthol and cooling listings.' },
            { value: 'dessert', icon: 'cup', label: 'Dessert or drink-style', text: 'Dessert, coffee, cola and drink-style listings.' },
          ],
        },
        shopping: {
          key: 'shopping',
          phase: 3,
          kicker: 'Final choice',
          signal: 'Available now',
          title: 'Is there anything else you would like to prioritise?',
          help: 'These options appear only when matching products currently carry the relevant offer or bundle information.',
          options: [
            { value: 'any', icon: 'sparkle', label: 'No extra preference', text: 'Show the strongest matches across the range.' },
            { value: 'offers', icon: 'sale', label: 'Current offers', text: 'Prioritise products with current offer information.' },
            { value: 'bundles', icon: 'bundle', label: 'Bundles or multipacks', text: 'Prioritise listed bundle and multipack formats.' },
          ],
        },
      };
    }

    activeSteps() {
      const definitions = this.definitions();
      const steps = [definitions.approach];
      if (!this.answers.approach || this.answers.approach === 'compare') return steps;

      steps.push(definitions.format);
      if (!this.answers.format) return steps;

      if (this.answers.format === 'not_sure') {
        steps.push(definitions.routePreference);
        if (!this.answers.routePreference || this.answers.routePreference === 'still_comparing') return steps;
      }

      const routeKey = this.effectiveFormat();
      if (!routeKey) return steps;

      if (routeKey === 'vape') {
        steps.push(definitions.vapeSubtype);
        if (!this.answers.vapeSubtype) return steps;
        steps.push(definitions.age);
        if (this.answers.age !== 'yes') return steps;
      }

      if (this.answers.approach === 'direct') return steps;

      const route = this.resolveRoute(routeKey);
      const isDeviceRoute = routeKey === 'vape' && this.answers.vapeSubtype === 'devices';
      if (!isDeviceRoute && this.shouldAskCbdType(route)) {
        steps.push(definitions.cbdType);
        if (!this.answers.cbdType) return steps;
      }

      if (this.shouldAskFlavour(route)) {
        steps.push(definitions.flavour);
        if (!this.answers.flavour) return steps;
      }

      if (this.shouldAskShopping(route)) steps.push(definitions.shopping);
      return steps;
    }

    effectiveFormat() {
      if (this.answers.format === 'not_sure') return this.answers.routePreference || '';
      return this.answers.format || '';
    }

    resolveRoute(routeKey = this.effectiveFormat()) {
      const base = this.config.routes?.[routeKey];
      if (!base) return null;
      const route = { ...base, key: routeKey, formats: [...(base.formats || [])] };

      if (routeKey === 'vape') {
        const subtype = this.answers.vapeSubtype || 'any';
        if (subtype === 'eliquid') route.formats = ['E-Liquid'];
        if (subtype === 'pods') route.formats = ['Vape Pod'];
        if (subtype === 'devices') route.formats = ['Vape Device', 'Vape Battery', 'Vape Accessory'];
      }
      return route;
    }

    isFlavourRelevant(routeKey) {
      if (['oils', 'gummies', 'drinks', 'extracts'].includes(routeKey)) return true;
      if (routeKey !== 'vape') return false;
      return this.answers.vapeSubtype !== 'devices';
    }

    isCatalogueReady(route) {
      return Boolean(route && this.currentFeed && this.currentFeed.handle === route.handle && Array.isArray(this.currentFeed.products));
    }

    shouldAskFlavour(route) {
      if (!route || !this.isFlavourRelevant(route.key)) return false;
      if (!this.isCatalogueReady(route)) return true;
      const counts = this.flavourCounts(route);
      return ['natural', 'fruit', 'mint', 'dessert'].some((key) => counts[key] > 0);
    }

    shouldAskCbdType(route) {
      if (!route) return false;
      if (!this.isCatalogueReady(route)) return true;
      const counts = this.cbdTypeCounts(route);
      return counts.full > 0 || counts.broad > 0 || counts.isolate > 0;
    }

    shouldAskShopping(route) {
      if (!route) return false;
      if (!this.isCatalogueReady(route)) return true;
      const counts = this.shoppingCounts(route);
      return counts.offers > 0 || counts.bundles > 0;
    }

    async onContinue(event) {
      event.preventDefault();
      const steps = this.activeSteps();
      const current = steps[this.stepIndex];
      if (!current) return;

      if (!this.answers[current.key]) {
        this.showError();
        this.stage.querySelector('input:not(:disabled)')?.focus();
        return;
      }

      this.trackStart('tool_interaction');
      this.trackStepCompletion(current);

      if (current.key === 'approach' && this.answers.approach === 'compare') {
        this.renderTypeEducationRoute();
        return;
      }

      if (current.key === 'age' && this.answers.age === 'no') {
        this.renderAgeStop();
        return;
      }

      if (this.shouldLoadCatalogueAfter(current)) {
        const route = this.resolveRoute();
        if (route) {
          const loaded = await this.ensureCatalogue(route);
          if (!loaded) return;
        }
      }

      this.advanceFromCurrent();
    }

    shouldLoadCatalogueAfter(step) {
      if (!step) return false;
      if (step.key === 'format') return this.answers.format !== 'not_sure';
      if (step.key === 'routePreference') return this.answers.routePreference !== 'still_comparing';
      return false;
    }

    advanceFromCurrent() {
      const steps = this.activeSteps();
      if (this.stepIndex < steps.length - 1) {
        this.stepIndex += 1;
        this.renderStep(true);
        return;
      }
      this.complete();
    }

    onBack() {
      if (this.stepIndex <= 0) return;
      const current = this.activeSteps()[this.stepIndex];
      this.publishAnalytics('back_clicked', {
        step_key: current?.key || 'unknown',
        phase: Number(current?.phase || 1),
      });
      this.stepIndex -= 1;
      this.renderStep(true);
    }

    async onRootClick(event) {
      const startLink = event.target.closest('[data-cbh-finder-start]');
      if (startLink) this.trackStart('hero_cta');

      const trackedLink = event.target.closest('a[href]');
      if (trackedLink && !startLink) this.trackResultClick(trackedLink);

      const retryCatalogue = event.target.closest('[data-cbh-retry-feed]');
      if (retryCatalogue) {
        event.preventDefault();
        this.publishAnalytics('catalogue_retry_clicked');
        await this.retryCatalogueLoad();
        return;
      }

      const retryResults = event.target.closest('[data-cbh-retry-results]');
      if (retryResults) {
        event.preventDefault();
        this.publishAnalytics('results_retry_clicked');
        await this.retryResultLoad();
        return;
      }

      const changeButton = event.target.closest('[data-cbh-change-answers]');
      if (changeButton) {
        event.preventDefault();
        const targetStep = changeButton.dataset.cbhChangeAnswers || 'format';
        this.publishAnalytics('answers_revisited', { target_step: targetStep });
        this.returnToAnswers(targetStep);
        return;
      }

      const restartButton = event.target.closest('[data-cbh-restart]');
      if (restartButton) {
        event.preventDefault();
        this.publishAnalytics('restarted');
        this.answers = {};
        this.stepIndex = 0;
        this.currentFeed = null;
        this.returnToForm();
        return;
      }

      const toggleRoutes = event.target.closest('[data-cbh-toggle-routes]');
      if (toggleRoutes) {
        event.preventDefault();
        const routeGrid = this.root.querySelector('[data-cbh-route-grid]');
        if (!routeGrid) return;
        const expanded = routeGrid.classList.toggle('is-expanded');
        toggleRoutes.setAttribute('aria-expanded', String(expanded));
        toggleRoutes.querySelector('span:first-child').textContent = expanded ? 'Show fewer formats' : 'Browse all CBD formats';
      }
    }

    renderStep(moveFocus = true) {
      const steps = this.activeSteps();
      this.stepIndex = Math.min(this.stepIndex, Math.max(steps.length - 1, 0));
      const step = steps[this.stepIndex];
      if (!step) return;

      const options = this.optionsFor(step);
      const validValues = new Set(options.filter((option) => !option.disabled).map((option) => option.value));
      if (this.answers[step.key] && !validValues.has(this.answers[step.key])) delete this.answers[step.key];
      const selected = this.answers[step.key] || '';

      const optionMarkup = options
        .map((option, index) => {
          const inputId = `${this.config.sectionId}-${step.key}-${index}`;
          const count = Number.isFinite(option.count)
            ? `<span class="cbh-native-finder__option-count">${option.count > 0 ? `${option.count} available` : 'Currently unavailable'}</span>`
            : '';
          return `
            <div class="cbh-native-finder__option${option.disabled ? ' is-unavailable' : ''}">
              <input
                type="radio"
                id="${escapeHtml(inputId)}"
                name="${escapeHtml(step.key)}"
                value="${escapeHtml(option.value)}"
                ${selected === option.value ? 'checked' : ''}
                ${option.disabled ? 'disabled' : ''}
              >
              <label for="${escapeHtml(inputId)}"${option.disabled ? ' aria-disabled="true"' : ''}>
                <span class="cbh-native-finder__option-icon" aria-hidden="true">${iconMarkup(option.icon)}</span>
                <span class="cbh-native-finder__option-copy">
                  <strong>${escapeHtml(option.label)}</strong>
                  <small>${escapeHtml(option.text)}</small>
                  ${count}
                </span>
                <span class="cbh-native-finder__option-check" aria-hidden="true">✓</span>
              </label>
            </div>`;
        })
        .join('');

      this.stage.innerHTML = `
        <div class="cbh-native-finder__question-topline">
          <span class="cbh-native-finder__question-kicker">${escapeHtml(step.kicker)}</span>
          <span class="cbh-native-finder__question-signal">${escapeHtml(step.signal)}</span>
        </div>
        <fieldset class="cbh-native-finder__fieldset${step.className ? ` cbh-native-finder__fieldset--${escapeHtml(step.className)}` : ''}">
          <legend tabindex="-1">${escapeHtml(step.title)}</legend>
          <p class="cbh-native-finder__question-help">${step.help}</p>
          <div class="cbh-native-finder__options">${optionMarkup}</div>
        </fieldset>`;

      this.stage.querySelectorAll('input[type="radio"]:not(:disabled)').forEach((input) => {
        input.addEventListener('change', () => this.onAnswer(step, input.value));
      });

      this.root.classList.remove('is-checking-catalogue');
      this.hideFeedStatus();
      this.hideError();
      this.controls.hidden = false;
      this.continueButton.hidden = false;
      this.continueButton.disabled = !selected;
      this.backButton.hidden = this.stepIndex === 0;
      this.updateContinueLabel(steps, step);
      this.renderProgress(step.phase || 3);
      this.renderAnswerSummary();

      if (this.live) this.live.textContent = `${PHASES[(step.phase || 1) - 1].label}: ${step.title}`;
      if (moveFocus) this.stage.querySelector('legend')?.focus({ preventScroll: true });
    }

    optionsFor(step) {
      const options = step.options.map((option) => ({ ...option }));
      const route = this.resolveRoute();
      if (!route || !this.isCatalogueReady(route)) return options;

      if (step.key === 'vapeSubtype') {
        const counts = this.vapeSubtypeCounts();
        return options.map((option) => ({ ...option, count: counts[option.value] || 0, disabled: (counts[option.value] || 0) === 0 }));
      }

      if (step.key === 'cbdType') {
        const counts = this.cbdTypeCounts(route);
        return options.map((option) => ({ ...option, count: counts[option.value] || 0, disabled: (counts[option.value] || 0) === 0 }));
      }

      if (step.key === 'flavour') {
        const counts = this.flavourCounts(route);
        return options
          .filter((option) => option.value === 'any' || (counts[option.value] || 0) > 0)
          .map((option) => ({ ...option, count: counts[option.value] || 0 }));
      }

      if (step.key === 'shopping') {
        const counts = this.shoppingCounts(route);
        return options
          .filter((option) => option.value === 'any' || (counts[option.value] || 0) > 0)
          .map((option) => ({ ...option, count: counts[option.value] || 0 }));
      }
      return options;
    }

    onAnswer(step, value) {
      if (step.key === 'approach' && this.answers.approach !== value) {
        this.clearAnswers(['format', 'routePreference', 'vapeSubtype', 'age', 'cbdType', 'flavour', 'shopping']);
        this.currentFeed = null;
      }
      if (step.key === 'format' && this.answers.format !== value) {
        this.clearAnswers(['routePreference', 'vapeSubtype', 'age', 'cbdType', 'flavour', 'shopping']);
        this.currentFeed = null;
      }
      if (step.key === 'routePreference' && this.answers.routePreference !== value) {
        this.clearAnswers(['vapeSubtype', 'age', 'cbdType', 'flavour', 'shopping']);
        this.currentFeed = null;
      }
      if (step.key === 'vapeSubtype' && this.answers.vapeSubtype !== value) {
        this.clearAnswers(['age', 'cbdType', 'flavour', 'shopping']);
      }
      if (step.key === 'cbdType' && this.answers.cbdType !== value) this.clearAnswers(['flavour', 'shopping']);
      if (step.key === 'flavour' && this.answers.flavour !== value) this.clearAnswers(['shopping']);

      this.answers[step.key] = value;
      this.hideError();
      this.continueButton.disabled = false;
      this.updateContinueLabel(this.activeSteps(), step);
      this.renderAnswerSummary();
    }

    clearAnswers(keys) {
      keys.forEach((key) => delete this.answers[key]);
    }

    updateContinueLabel(steps, step) {
      const label = this.continueButton?.querySelector('span:first-child');
      if (!label) return;
      if (!this.answers[step.key]) {
        label.textContent = 'Continue';
        return;
      }
      if (step.key === 'approach' && this.answers.approach === 'compare') {
        label.textContent = 'Compare CBD Types';
        return;
      }
      if (this.stepIndex !== steps.length - 1) {
        label.textContent = 'Continue';
        return;
      }
      label.textContent = this.answers.approach === 'direct' ? 'View Products' : 'View Matches';
    }

    renderProgress(phase = 1, complete = false) {
      const current = complete ? PHASES.length : Math.max(1, Math.min(PHASES.length, phase));
      const percentage = (current / PHASES.length) * 100;

      if (this.progressLabel) this.progressLabel.textContent = complete ? 'Matches ready' : PHASES[current - 1].label;
      if (this.progressCount) this.progressCount.textContent = `${current} of ${PHASES.length}`;
      if (this.progressBar) this.progressBar.style.width = `${percentage}%`;

      if (!this.stepList) return;
      this.stepList.innerHTML = PHASES
        .map((item, index) => {
          const number = index + 1;
          const state = complete || number < current ? 'is-complete' : number === current ? 'is-active' : '';
          const marker = complete || number < current ? '✓' : String(number).padStart(2, '0');
          const currentAttribute = !complete && number === current ? ' aria-current="step"' : '';
          return `<li class="${state}"${currentAttribute}><span class="cbh-native-finder__step-number" aria-hidden="true">${marker}</span><span><strong>${escapeHtml(item.label)}</strong><small>${escapeHtml(item.description)}</small></span></li>`;
        })
        .join('');
    }

    renderAnswerSummary() {
      if (!this.answerSummary) return;
      const labels = [];
      const routeKey = this.effectiveFormat();
      if (routeKey && routeKey !== 'still_comparing') labels.push(formatLabels[routeKey]);
      if (this.answers.cbdType && this.answers.cbdType !== 'any') labels.push(typeLabels[this.answers.cbdType]);
      if (this.answers.flavour && this.answers.flavour !== 'any') labels.push(flavourLabels[this.answers.flavour]);
      if (this.answers.shopping && this.answers.shopping !== 'any') labels.push(shoppingLabels[this.answers.shopping]);

      const list = this.answerSummary.querySelector('ul');
      this.answerSummary.hidden = labels.length === 0;
      if (list) list.innerHTML = labels.map((label) => `<li>${escapeHtml(label)}</li>`).join('');
    }

    showError() {
      if (this.error) this.error.hidden = false;
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }

    showFeedStatus(text = 'Checking current products and available choices…') {
      if (!this.feedStatus) return;
      this.feedStatus.hidden = false;
      const copy = this.feedStatus.querySelector('span:last-child');
      if (copy) copy.textContent = text;
    }

    hideFeedStatus() {
      if (this.feedStatus) this.feedStatus.hidden = true;
    }

    async ensureCatalogue(route, force = false) {
      this.root.classList.add('is-checking-catalogue');
      this.root.dataset.cbhFeedState = 'loading';
      this.continueButton.disabled = true;
      const label = this.continueButton.querySelector('span:first-child');
      if (label) label.textContent = 'Checking products…';
      this.showFeedStatus();
      if (this.live) this.live.textContent = `Checking current products for ${route.title}.`;

      try {
        const feed = await this.fetchFeed(route.handle, { force });
        this.currentFeed = { handle: route.handle, products: feed.products, collection: feed.collection || {} };
        this.pendingCatalogue = null;
        this.root.dataset.cbhFeedState = 'ready';
        this.root.classList.remove('is-checking-catalogue');
        this.hideFeedStatus();
        return true;
      } catch (error) {
        this.currentFeed = null;
        this.pendingCatalogue = { route, error };
        this.root.dataset.cbhFeedState = 'error';
        this.root.classList.remove('is-checking-catalogue');
        this.hideFeedStatus();
        console.error('CBD Product Finder catalogue check failed.', { code: error.code || 'unknown', status: error.status || 0, error });
        this.publishAnalytics('catalogue_error', {
          error_code: error.code || 'unknown',
          http_status: Number(error.status || 0),
        });
        this.renderCatalogueError(route, error);
        return false;
      }
    }

    async retryCatalogueLoad() {
      if (!this.pendingCatalogue?.route) return;
      const route = this.pendingCatalogue.route;
      this.renderCatalogueRetrying(route);
      const loaded = await this.ensureCatalogue(route, true);
      if (loaded) this.advanceFromCurrent();
    }

    renderCatalogueRetrying(route) {
      this.stage.innerHTML = `
        <div class="cbh-native-finder__catalogue-check" role="status">
          <span class="cbh-native-finder__loader" aria-hidden="true"></span>
          <p><strong>Checking ${escapeHtml(route.title)}</strong><small>Loading current availability and product choices.</small></p>
        </div>`;
      this.controls.hidden = true;
    }

    renderCatalogueError(route, error) {
      const code = escapeHtml(error?.code || 'unknown');
      this.stage.innerHTML = `
        <div class="cbh-native-finder__catalogue-error" data-cbh-feed-error="${code}">
          <span class="cbh-native-finder__catalogue-error-icon" aria-hidden="true">${iconMarkup('question')}</span>
          <p class="cbh-native-finder__question-kicker">We could not check availability</p>
          <h3 tabindex="-1">Let’s Try That Product Check Again</h3>
          <p>The finder could not load the current choices for this format. You can try again, browse the full range or choose another format.</p>
          <div class="cbh-native-finder__result-actions">
            <button class="cbh-native-finder__button cbh-native-finder__button--solid" type="button" data-cbh-retry-feed>Try Again</button>
            <a class="cbh-native-finder__button cbh-native-finder__button--soft" href="${escapeHtml(safeUrl(route.url))}">Browse ${escapeHtml(route.title)}</a>
            <button class="cbh-native-finder__back" type="button" data-cbh-change-answers="format">Choose Another Format</button>
          </div>
        </div>`;
      this.controls.hidden = true;
      this.renderProgress(2);
      this.stage.querySelector('h3')?.focus({ preventScroll: true });
      if (this.live) this.live.textContent = 'Current product availability could not be checked. Try again or browse the matching range.';
    }

    async fetchFeed(handle, { force = false } = {}) {
      if (force) FEED_CACHE.delete(handle);
      if (!force && FEED_CACHE.has(handle)) return FEED_CACHE.get(handle);

      if (window.CBH_NATIVE_FINDER_FEEDS && window.CBH_NATIVE_FINDER_FEEDS[handle]) {
        const testFeed = window.CBH_NATIVE_FINDER_FEEDS[handle];
        if (!testFeed || !Array.isArray(testFeed.products)) throw new FinderFeedError('invalid_feed', 'The supplied product feed is invalid.');
        FEED_CACHE.set(handle, testFeed);
        return testFeed;
      }

      let lastError;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const feed = await this.requestFeed(handle);
          FEED_CACHE.set(handle, feed);
          return feed;
        } catch (error) {
          lastError = error;
          if (attempt === 0) await wait(240);
        }
      }
      throw lastError || new FinderFeedError('unknown', 'The product feed could not be loaded.');
    }

    async requestFeed(handle) {
      const rootUrl = String(this.config.rootUrl || '/').replace(/\/?$/, '/');
      const endpoint = new URL(`${rootUrl}collections/${encodeURIComponent(handle)}`, window.location.origin);
      endpoint.searchParams.set('view', this.config.feedView || 'finder-feed');

      let response;
      try {
        response = await fetch(endpoint.toString(), {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
      } catch (error) {
        throw new FinderFeedError('network', 'The catalogue request could not be completed.');
      }

      if (!response.ok) throw new FinderFeedError('http', `Product feed returned ${response.status}.`, response.status);
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const payload = documentFragment.querySelector('[data-cbh-finder-feed-json]');
      if (!payload) throw new FinderFeedError('missing_payload', 'The product feed payload was missing.');

      let feed;
      try {
        feed = JSON.parse(payload.textContent);
      } catch (error) {
        throw new FinderFeedError('invalid_json', 'The product feed could not be read.');
      }
      if (!feed || !Array.isArray(feed.products)) throw new FinderFeedError('invalid_feed', 'The product feed did not contain a product list.');
      return feed;
    }

    catalogueProducts(route, { includeType = false } = {}) {
      if (!this.isCatalogueReady(route)) return [];
      return this.currentFeed.products
        .filter((product) => product && product.available && product.variantAvailable)
        .filter((product) => this.isCbdProduct(product))
        .filter((product) => this.formatMatches(product, route.formats))
        .filter((product) => {
          if (!includeType) return true;
          const selectedType = this.answers.cbdType;
          if (!selectedType || selectedType === 'any') return true;
          return this.typeMatches(product.cbdType, selectedType);
        });
    }

    vapeSubtypeCounts() {
      const base = this.config.routes?.vape;
      if (!base) return { any: 0, eliquid: 0, pods: 0, devices: 0 };
      const all = this.catalogueProducts({ ...base, key: 'vape', formats: [...(base.formats || [])] });
      return {
        any: all.length,
        eliquid: all.filter((product) => this.formatMatches(product, ['E-Liquid'])).length,
        pods: all.filter((product) => this.formatMatches(product, ['Vape Pod'])).length,
        devices: all.filter((product) => this.formatMatches(product, ['Vape Device', 'Vape Battery', 'Vape Accessory'])).length,
      };
    }

    cbdTypeCounts(route) {
      const products = this.catalogueProducts(route);
      return {
        any: products.length,
        full: products.filter((product) => this.typeMatches(product.cbdType, 'full')).length,
        broad: products.filter((product) => this.typeMatches(product.cbdType, 'broad')).length,
        isolate: products.filter((product) => this.typeMatches(product.cbdType, 'isolate')).length,
      };
    }

    flavourCounts(route) {
      const products = this.catalogueProducts(route, { includeType: true });
      return {
        any: products.length,
        natural: products.filter((product) => this.flavourMatches(product.flavour, 'natural')).length,
        fruit: products.filter((product) => this.flavourMatches(product.flavour, 'fruit')).length,
        mint: products.filter((product) => this.flavourMatches(product.flavour, 'mint')).length,
        dessert: products.filter((product) => this.flavourMatches(product.flavour, 'dessert')).length,
      };
    }

    shoppingBaseProducts(route) {
      const products = this.catalogueProducts(route, { includeType: true });
      const selectedFlavour = this.answers.flavour;
      if (!selectedFlavour || selectedFlavour === 'any') return products;
      const exactFlavour = products.filter((product) => this.flavourMatches(product.flavour, selectedFlavour));
      return exactFlavour.length ? exactFlavour : products;
    }

    shoppingCounts(route) {
      const products = this.shoppingBaseProducts(route);
      return {
        any: products.length,
        offers: products.filter((product) => this.hasCurrentOffer(product)).length,
        bundles: products.filter((product) => this.hasBundle(product)).length,
      };
    }

    complete() {
      const routeKey = this.effectiveFormat();
      if (routeKey === 'still_comparing') {
        this.renderEducationRoute();
        return;
      }

      const route = this.resolveRoute(routeKey);
      if (!route) {
        this.renderEducationRoute();
        return;
      }

      this.root.classList.add('has-results');
      this.form.hidden = true;
      this.results.hidden = false;
      this.renderProgress(4, true);
      this.results.innerHTML = this.routeMarkup(route);
      this.results.querySelector('h2')?.focus({ preventScroll: true });
      this.loadProducts(route);
    }

    routeMarkup(route) {
      const chips = this.selectionChips(route);
      const choicesOpen = window.matchMedia('(min-width: 750px)').matches ? ' open' : '';
      const browseLabel = browseLabels[route.key] || route.title;

      return `
        <section class="cbh-native-finder__result-route" aria-labelledby="${escapeHtml(this.config.sectionId)}-result-heading">
          <div>
            <p class="cbh-native-finder__result-kicker">Your product matches</p>
            <h2 id="${escapeHtml(this.config.sectionId)}-result-heading" tabindex="-1">Your Product Matches</h2>
            <ul class="cbh-native-finder__result-chips">${chips.map((chip) => `<li>${escapeHtml(chip)}</li>`).join('')}</ul>
            <p class="cbh-native-finder__result-intro">Compare the available products below, then open the product page to check the current label, strength, variants and full details.</p>
            <div class="cbh-native-finder__result-actions">
              <a class="cbh-native-finder__result-action cbh-native-finder__result-action--primary" href="${escapeHtml(safeUrl(route.url))}">Browse all ${escapeHtml(browseLabel)} <span aria-hidden="true">→</span></a>
              <button class="cbh-native-finder__result-action cbh-native-finder__result-action--secondary" type="button" data-cbh-change-answers="format">Change Answers</button>
            </div>
          </div>
          <details class="cbh-native-finder__result-reasons"${choicesOpen}>
            <summary><span class="cbh-native-finder__choices-show">View your choices</span><span class="cbh-native-finder__choices-hide">Hide your choices</span><span aria-hidden="true">⌄</span></summary>
            <div class="cbh-native-finder__result-reasons-body">
              <h3>What you selected</h3>
              <ul>${this.selectionSummary(route).map((reason) => `<li>${escapeHtml(reason)}</li>`).join('')}</ul>
            </div>
          </details>
        </section>
        <section class="cbh-native-finder__matches" data-cbh-matches aria-labelledby="${escapeHtml(this.config.sectionId)}-matches-heading">
          <div class="cbh-native-finder__matches-head">
            <div>
              <h3 id="${escapeHtml(this.config.sectionId)}-matches-heading">Preparing Your Matches</h3>
              <p>Checking current availability and product details.</p>
            </div>
          </div>
          <div class="cbh-native-finder__skeleton-grid" aria-hidden="true">
            <div class="cbh-native-finder__skeleton"></div>
            <div class="cbh-native-finder__skeleton"></div>
            <div class="cbh-native-finder__skeleton"></div>
          </div>
        </section>`;
    }

    selectionChips(route) {
      const chips = [route.title];
      if (this.answers.cbdType && this.answers.cbdType !== 'any') chips.push(typeLabels[this.answers.cbdType]);
      if (this.answers.flavour && this.answers.flavour !== 'any') chips.push(flavourLabels[this.answers.flavour]);
      if (this.answers.shopping && this.answers.shopping !== 'any') chips.push(shoppingLabels[this.answers.shopping]);
      return chips;
    }

    selectionSummary(route) {
      const summary = [`Format: ${route.title}`];
      if (route.key === 'vape' && this.answers.vapeSubtype && this.answers.vapeSubtype !== 'any') {
        const labels = { eliquid: 'CBD e-liquids', pods: 'pods and cartridges', devices: 'devices and accessories' };
        summary.push(`Vape format: ${labels[this.answers.vapeSubtype]}`);
      }
      if (this.answers.cbdType && this.answers.cbdType !== 'any') summary.push(`CBD type: ${typeLabels[this.answers.cbdType]}`);
      if (this.answers.cbdType === 'any') summary.push('CBD type: no preference');
      if (this.answers.flavour && this.answers.flavour !== 'any') summary.push(`Flavour: ${flavourLabels[this.answers.flavour]}`);
      if (this.answers.shopping && this.answers.shopping !== 'any') summary.push(`Priority: ${shoppingLabels[this.answers.shopping]}`);
      return summary;
    }

    resultSupportMarkup() {
      const links = [];
      if (this.answers.approach === 'guided') {
        links.push(`<a href="${escapeHtml(safeUrl(this.config.guides?.startHere, '/blogs/brand-product-spotlights/cbd-products-uk-guide'))}">CBD Beginner’s Guide <span aria-hidden="true">→</span></a>`);
        const compareUrl = safeUrl(this.config.guides?.compareTypes, safeUrl(this.config.guides?.allCbd, '/collections/all-cbd-products'));
        const compareLabel = this.isExpectedGuide(compareUrl, '/pages/compare-cbd-types') ? 'Compare CBD Types' : 'Browse All CBD Products';
        links.push(`<a href="${escapeHtml(compareUrl)}">${escapeHtml(compareLabel)} <span aria-hidden="true">→</span></a>`);
      }
      const strengthUrl = safeUrl(this.config.guides?.strength, '');
      if (strengthUrl) {
        links.push(`<a href="${escapeHtml(strengthUrl)}">Strength & Serving Guide <span aria-hidden="true">→</span></a>`);
      }
      return `<nav class="cbh-native-finder__result-support cbh-native-finder__result-support--matches" aria-label="Useful CBD guides">${links.join('')}</nav>`;
    }

    async loadProducts(route, force = false) {
      const matches = this.results.querySelector('[data-cbh-matches]');
      if (!matches) return;

      try {
        if (!this.isCatalogueReady(route) || force) {
          const feed = await this.fetchFeed(route.handle, { force });
          this.currentFeed = { handle: route.handle, products: feed.products, collection: feed.collection || {} };
        }
        const groups = this.rankProducts(this.currentFeed.products || [], route);
        matches.outerHTML = this.productsMarkup(groups, route);
        const total = groups.exact.length + groups.alternatives.length;
        this.trackResultView('product_matches', {
          exact_count: groups.exact.length,
          alternative_count: groups.alternatives.length,
          shown_count: total,
          available_count: groups.hardEligibleCount,
        });
        if (this.live) {
          this.live.textContent = total
            ? `${total} available product matches loaded for ${route.title}.`
            : `No available products matched the selected details for ${route.title}.`;
        }
        this.pendingResultRoute = null;
      } catch (error) {
        console.error('CBD Product Finder results could not be loaded.', { code: error.code || 'unknown', status: error.status || 0, error });
        this.publishAnalytics('results_error', {
          error_code: error.code || 'unknown',
          http_status: Number(error.status || 0),
        });
        this.pendingResultRoute = route;
        matches.outerHTML = this.loadErrorMarkup(route, error);
        if (this.live) this.live.textContent = 'Current product matches could not be loaded. Try again or browse the full range.';
      }
    }

    async retryResultLoad() {
      if (!this.pendingResultRoute) return;
      const route = this.pendingResultRoute;
      const errorSection = this.results.querySelector('.cbh-native-finder__load-error')?.closest('section');
      if (errorSection) {
        errorSection.outerHTML = `
          <section class="cbh-native-finder__matches" data-cbh-matches>
            <div class="cbh-native-finder__catalogue-check">
              <span class="cbh-native-finder__loader" aria-hidden="true"></span>
              <p><strong>Checking current products again</strong><small>Loading availability and product details.</small></p>
            </div>
          </section>`;
      }
      await this.loadProducts(route, true);
    }

    rankProducts(products, route) {
      const hardEligible = products
        .filter((product) => product && product.available && product.variantAvailable)
        .filter((product) => this.isCbdProduct(product))
        .filter((product) => this.formatMatches(product, route.formats))
        .filter((product) => {
          const selectedType = this.answers.cbdType;
          if (!selectedType || selectedType === 'any') return true;
          return this.typeMatches(product.cbdType, selectedType);
        });

      const softPreferences = this.hasSoftPreferences();
      const scored = hardEligible
        .map((product) => ({ ...product, finderScore: this.productScore(product), exactPreferenceMatch: this.softPreferencesMatch(product) }))
        .sort((a, b) => b.finderScore - a.finderScore || Number(a.rank || 0) - Number(b.rank || 0));

      const exactCandidates = softPreferences ? scored.filter((product) => product.exactPreferenceMatch) : scored;
      const alternativeCandidates = softPreferences ? scored.filter((product) => !product.exactPreferenceMatch) : [];
      const exact = this.selectWithMeasuredDiversity(exactCandidates, 3);
      const remaining = Math.max(0, 3 - exact.length);
      const alternatives = remaining ? this.selectWithMeasuredDiversity(alternativeCandidates, remaining) : [];

      return {
        exact,
        alternatives,
        hardEligibleCount: hardEligible.length,
        exactAvailableCount: exactCandidates.length,
        hasSoftPreferences: softPreferences,
      };
    }

    productScore(product) {
      let score = 100;
      const selectedFlavour = this.answers.flavour;
      const shopping = this.answers.shopping;
      if (selectedFlavour && selectedFlavour !== 'any' && this.flavourMatches(product.flavour, selectedFlavour)) score += 32;
      if (shopping === 'offers' && this.hasCurrentOffer(product)) score += 36;
      if (shopping === 'bundles' && this.hasBundle(product)) score += 36;
      if (asText(product.cbdType)) score += 5;
      if (asText(product.strength) || asText(product.totalCbd)) score += 5;
      if (asText(product.flavour)) score += 3;
      if (asText(product.shortIntro)) score += 2;
      score -= Number(product.rank || 0) * 0.025;
      return score;
    }

    hasSoftPreferences() {
      return Boolean(
        (this.answers.flavour && this.answers.flavour !== 'any') ||
        (this.answers.shopping && this.answers.shopping !== 'any')
      );
    }

    softPreferencesMatch(product) {
      if (this.answers.flavour && this.answers.flavour !== 'any' && !this.flavourMatches(product.flavour, this.answers.flavour)) return false;
      if (this.answers.shopping === 'offers' && !this.hasCurrentOffer(product)) return false;
      if (this.answers.shopping === 'bundles' && !this.hasBundle(product)) return false;
      return true;
    }

    selectWithMeasuredDiversity(products, limit) {
      const pool = [...products];
      const selected = [];
      const vendors = new Set();

      while (pool.length && selected.length < limit) {
        let choiceIndex = 0;
        const leading = pool[0];
        const leadingVendor = normalise(leading.vendor);
        if (leadingVendor && vendors.has(leadingVendor)) {
          const alternativeIndex = pool.findIndex((product) => {
            const vendor = normalise(product.vendor);
            return vendor && !vendors.has(vendor) && leading.finderScore - product.finderScore <= 6;
          });
          if (alternativeIndex > 0) choiceIndex = alternativeIndex;
        }
        const [choice] = pool.splice(choiceIndex, 1);
        selected.push(choice);
        const vendor = normalise(choice.vendor);
        if (vendor) vendors.add(vendor);
      }
      return selected;
    }

    isCbdProduct(product) {
      const category = normalise(product.filterCategory);
      return category === 'cbd' || category.startsWith('cbd ');
    }

    formatMatches(product, expectedFormats) {
      const actual = normalise(product.filterFormat);
      if (!actual) return false;
      return expectedFormats.some((format) => actual === normalise(format));
    }

    typeMatches(value, selected) {
      const actual = normalise(value);
      if (!actual) return false;
      if (selected === 'full') return actual.includes('full spectrum');
      if (selected === 'broad') return actual.includes('broad spectrum');
      if (selected === 'isolate') return actual === 'isolate' || actual.includes('cbd isolate');
      return true;
    }

    flavourMatches(value, selected) {
      const flavour = normalise(value);
      if (!flavour) return false;
      const groups = new Set();
      if (/unflavo|neutral|natural|not applicable|hemp|botanical/.test(flavour)) groups.add('natural');
      if (/fruit|berry|strawberry|raspberry|cherry|apple|orange|mango|peach|pineapple|lemon|lime|grape|watermelon|tropical|pina|citrus/.test(flavour)) groups.add('fruit');
      if (/mint|menthol|peppermint|spearmint|cool|cooling|ice|arctic/.test(flavour)) groups.add('mint');
      if (/dessert|cream|biscuit|cheesecake|candy|coffee|cola|cocoa|chocolate|vanilla|caramel|lemonade|energy drink|bubblegum|sherbet/.test(flavour)) groups.add('dessert');
      return groups.has(selected);
    }

    hasCurrentOffer(product) {
      const offerText = normalise(product.offers);
      const tags = normalise(product.tags);
      return Boolean(offerText || /offer|bogof|sale|clearance/.test(tags) || Number(product.compareAtPrice || 0) > Number(product.price || 0));
    }

    hasBundle(product) {
      const combined = normalise([product.offers, product.tags, product.title, product.filterFormat, product.strength]);
      return /bundle|multi pack|multipack|twin pack|two bottle|second bottle|three product|three tub|three pack|\d+ capsule option/.test(combined);
    }

    productsMarkup(groups, route) {
      const headingId = `${this.config.sectionId}-matches-heading`;
      if (!groups.hardEligibleCount) return this.emptyProductsMarkup(route, headingId);

      const totalShown = groups.exact.length + groups.alternatives.length;
      const sections = [];
      let alternativeMarkup = '';
      if (groups.exact.length) {
        sections.push(this.productGroupMarkup({
          products: groups.exact,
          route,
          kind: 'exact',
          heading: 'Exact Matches',
          text: groups.hasSoftPreferences
            ? `${groups.exactAvailableCount} available product${groups.exactAvailableCount === 1 ? '' : 's'} matched every selected preference.`
            : 'These products match your selected format and any CBD type preference you chose.',
          startIndex: 0,
        }));
      }
      if (groups.alternatives.length) {
        const gaps = unique(groups.alternatives.flatMap((product) => this.preferenceGaps(product)));
        alternativeMarkup = this.productGroupMarkup({
          products: groups.alternatives,
          route,
          kind: 'alternative',
          heading: 'Closest Alternatives',
          text: `No additional products matched every preference. These options still match your format${this.answers.cbdType && this.answers.cbdType !== 'any' ? ' and CBD type' : ''}${gaps.length ? `, but not ${gaps.join(' or ').toLowerCase()}` : ''}.`,
          startIndex: groups.exact.length,
        });
        if (!groups.exact.length) sections.push(alternativeMarkup);
      }
      sections.push(this.resultSupportMarkup());
      if (groups.exact.length && alternativeMarkup) sections.push(alternativeMarkup);

      return `
        <section class="cbh-native-finder__matches" aria-labelledby="${escapeHtml(headingId)}">
          <div class="cbh-native-finder__matches-head">
            <div>
              <h3 id="${escapeHtml(headingId)}">${totalShown} product${totalShown === 1 ? '' : 's'} shown</h3>
              <p>Exact matches are kept separate from close alternatives.</p>
            </div>
          </div>
          ${sections.join('')}
          <p class="cbh-native-finder__product-note">These are shopping matches based on current product data, not personal recommendations. Check the product page, label, ingredients, price and selected variant before ordering.</p>
        </section>`;
    }

    productGroupMarkup({ products, route, kind, heading, text, startIndex }) {
      return `
        <div class="cbh-native-finder__match-group cbh-native-finder__match-group--${escapeHtml(kind)}">
          <div class="cbh-native-finder__match-group-head">
            <span class="cbh-native-finder__match-group-icon" aria-hidden="true">${iconMarkup(kind === 'exact' ? 'check' : 'compare')}</span>
            <div><h4>${escapeHtml(heading)}</h4><p>${escapeHtml(text)}</p></div>
          </div>
          <div class="cbh-native-finder__product-grid">
            ${products.map((product, index) => this.productCardMarkup(product, route, startIndex + index, kind)).join('')}
          </div>
        </div>`;
    }

    productCardMarkup(product, route, index, kind) {
      const facts = unique([asText(product.filterFormat), asText(product.cbdType), this.compactStrengthFact(product)]).slice(0, 3);
      const reasons = this.productReasons(product, route).slice(0, 3);
      const gaps = kind === 'alternative' ? this.preferenceGaps(product) : [];
      const productUrl = safeUrl(product.url, route.url);
      const formattedPrice = this.formatMoney(product.price, product.currency);
      const formattedCompareAt = this.formatMoney(product.compareAtPrice, product.currency);
      const priceLabel = `${product.priceVaries ? 'From ' : ''}${formattedPrice}`;
      const compareAt = Number(product.compareAtPrice || 0) > Number(product.price || 0) && formattedCompareAt
        ? `<s>${escapeHtml(formattedCompareAt)}</s>`
        : '';
      const image = product.image
        ? `<img src="${escapeHtml(safeUrl(product.image, product.image))}" alt="${escapeHtml(product.imageAlt || product.title)}" width="720" height="620" loading="lazy">`
        : '<span class="cbh-native-finder__product-placeholder" aria-hidden="true">CBD</span>';
      const quickAdd = product.quickAdd && product.variantId ? this.quickAddMarkup(product) : '';

      return `
        <article
          class="cbh-native-finder__product-card"
          data-cbh-product-id="${escapeHtml(product.id)}"
          data-cbh-product-handle="${escapeHtml(product.handle)}"
          data-cbh-match-kind="${escapeHtml(kind)}"
          data-cbh-result-position="${index + 1}"
        >
          <a class="cbh-native-finder__product-media" href="${escapeHtml(productUrl)}" tabindex="-1" aria-hidden="true">
            ${image}
            <span class="cbh-native-finder__product-rank">0${index + 1}</span>
            <span class="cbh-native-finder__product-match-label cbh-native-finder__product-match-label--${escapeHtml(kind)}">${kind === 'exact' ? 'Exact match' : 'Closest alternative'}</span>
          </a>
          <div class="cbh-native-finder__product-copy">
            <p class="cbh-native-finder__product-vendor">${escapeHtml(product.vendor || 'The CBD Hut')}</p>
            <h5 class="cbh-native-finder__product-title"><a href="${escapeHtml(productUrl)}">${escapeHtml(product.title)}</a></h5>
            ${facts.length ? `<ul class="cbh-native-finder__product-facts">${facts.map((fact) => `<li>${escapeHtml(fact)}</li>`).join('')}</ul>` : ''}
            ${gaps.length ? `<p class="cbh-native-finder__preference-gap"><strong>Does not match:</strong> ${escapeHtml(gaps.join(', '))}</p>` : ''}
            <div class="cbh-native-finder__why-match">
              <strong>Why this product appears</strong>
              <ul>${reasons.map((reason) => `<li>${escapeHtml(reason)}</li>`).join('')}</ul>
            </div>
            <div class="cbh-native-finder__product-bottom">
              <div class="cbh-native-finder__product-price"><span>${escapeHtml(priceLabel)}</span>${compareAt}</div>
              <div class="cbh-native-finder__product-actions">
                <a class="cbh-native-finder__product-link" href="${escapeHtml(productUrl)}">View Product Details</a>
                ${quickAdd}
              </div>
            </div>
          </div>
        </article>`;
    }

    totalCbdLabel(product) {
      const total = asText(product.totalCbd);
      if (!total) return '';
      return /mg/i.test(total) ? total : `${total}mg total CBD`;
    }

    formatMoney(value, currency) {
      const cents = Number(value);
      if (!Number.isFinite(cents) || cents < 0) return '';
      const currencyCode = /^[A-Z]{3}$/.test(asText(currency).toUpperCase()) ? asText(currency).toUpperCase() : 'GBP';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(cents / 100);
      } catch (error) {
        return `£${(cents / 100).toFixed(2)}`;
      }
    }

    shortenDetail(value, maximum = 64) {
      const text = asText(value).replace(/\s*\|\s*/g, ' · ').replace(/\s+/g, ' ').trim();
      if (text.length <= maximum) return text;
      const clipped = text.slice(0, maximum + 1);
      const boundary = clipped.lastIndexOf(' ');
      return `${clipped.slice(0, boundary > 28 ? boundary : maximum).trim()}…`;
    }

    compactStrengthFact(product) {
      const strength = asText(product.strength);
      if (!strength) return this.totalCbdLabel(product);
      const parts = strength.split('|').map((part) => part.trim()).filter(Boolean);
      if (!parts.length) return this.totalCbdLabel(product);
      const first = parts[0]
        .replace(/approximately/ig, 'Approx.')
        .replace(/(\d+(?:\.\d+)?mg)\s+total cannabinoids per capsule/i, '$1 cannabinoids per capsule')
        .replace(/\s+/g, ' ')
        .trim();
      const pack = parts.slice(1).find((part) => /\b(?:capsules?|tablets?|gummies?|patches?|bottles?|packs?)\b/i.test(part) && part.length <= 28);
      return this.shortenDetail(pack && first.length + pack.length <= 58 ? `${first} · ${pack}` : first, 58);
    }

    offerDetail(product) {
      const offer = asText(product.offers);
      const normalisedOffer = normalise(offer);
      if (/buy one.*second bottle.*half price|second bottle.*half price/.test(normalisedOffer)) return 'Second bottle half price through the two-bottle option';
      if (/buy one get one free|\bbogof\b/.test(normalisedOffer)) return 'BOGOF offer listed';
      const valueOption = normalisedOffer.match(/(\d+) capsule option.*better value/);
      if (valueOption) return `${valueOption[1]}-capsule option offers better value`;
      if (offer) return `Offer: ${this.shortenDetail(offer, 58)}`;

      const tags = normalise(product.tags);
      if (/\bbogof\b/.test(tags)) return 'BOGOF offer listed';
      if (/clearance/.test(tags)) return 'Clearance price listed';
      if (/\bsale\b/.test(tags) || Number(product.compareAtPrice || 0) > Number(product.price || 0)) return 'Sale price available';
      return '';
    }

    bundleDetail(product) {
      const source = normalise([product.offers, product.tags, product.title, product.strength]);
      if (/two bottle|second bottle/.test(source)) return 'Two-bottle option available';
      if (/twin pack/.test(source)) return 'Twin-pack option available';
      if (/multi pack|multipack/.test(source)) return 'Multipack option available';
      const capsuleOption = source.match(/(\d+) capsule option/);
      if (capsuleOption) return `${capsuleOption[1]}-capsule option available`;
      if (/bundle/.test(source)) return 'Bundle option available';
      return 'Multipack option available';
    }

    productReasons(product, route) {
      const reasons = [`Format: ${asText(product.filterFormat) || formatLabels[route.key] || 'chosen CBD format'}`];
      if (this.answers.cbdType && this.answers.cbdType !== 'any') reasons.push(`CBD type: ${typeLabels[this.answers.cbdType]}`);
      else if (asText(product.cbdType)) reasons.push(`CBD type: ${asText(product.cbdType)}`);
      if (this.answers.flavour && this.answers.flavour !== 'any' && this.flavourMatches(product.flavour, this.answers.flavour)) {
        reasons.push(`Flavour: ${this.shortenDetail(product.flavour || flavourLabels[this.answers.flavour], 46)}`);
      }
      if (this.answers.shopping === 'offers' && this.hasCurrentOffer(product)) reasons.push(this.offerDetail(product));
      if (this.answers.shopping === 'bundles' && this.hasBundle(product)) reasons.push(this.bundleDetail(product));
      const strength = this.compactStrengthFact(product);
      if (reasons.length < 2 && strength) reasons.push(`Strength: ${strength}`);
      return unique(reasons);
    }

    preferenceGaps(product) {
      const gaps = [];
      if (this.answers.flavour && this.answers.flavour !== 'any' && !this.flavourMatches(product.flavour, this.answers.flavour)) gaps.push(flavourLabels[this.answers.flavour]);
      if (this.answers.shopping === 'offers' && !this.hasCurrentOffer(product)) gaps.push('Current offers');
      if (this.answers.shopping === 'bundles' && !this.hasBundle(product)) gaps.push('Bundles or multipacks');
      return gaps;
    }

    quickAddMarkup(product) {
      const formId = `${this.config.sectionId}-finder-product-${product.id}`;
      return `
        <product-form data-hide-errors="false">
          <form method="post" action="${escapeHtml(safeUrl(this.config.cartAddUrl, '/cart/add'))}" id="${escapeHtml(formId)}" accept-charset="UTF-8" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
            <input type="hidden" name="form_type" value="product">
            <input type="hidden" name="utf8" value="✓">
            <input type="hidden" name="id" value="${escapeHtml(product.variantId)}">
            <button class="cbh-native-finder__quick-add" type="submit" name="add">
              <span class="cbh-atc__label">Quick Add</span>
              <span class="sold-out-message hidden">Sold out</span>
              <span class="loading__spinner hidden" aria-hidden="true"></span>
            </button>
            <div class="product-form__error-message-wrapper" role="alert" hidden>
              <span class="product-form__error-message"></span>
            </div>
          </form>
        </product-form>`;
    }

    emptyProductsMarkup(route, headingId) {
      const exactType = this.answers.cbdType && this.answers.cbdType !== 'any';
      return `
        <section class="cbh-native-finder__matches" aria-labelledby="${escapeHtml(headingId)}">
          <div class="cbh-native-finder__empty">
            <span class="cbh-native-finder__catalogue-error-icon" aria-hidden="true">${iconMarkup('compare')}</span>
            <h3 id="${escapeHtml(headingId)}">${exactType ? `No Available ${escapeHtml(typeLabels[this.answers.cbdType])} Products Matched` : 'No Available Products Matched These Choices'}</h3>
            <p>${exactType ? `Your CBD type remains an exact choice, so products with another profile have not been substituted.` : 'Try changing one preference or browse the full format range to review every current product.'}</p>
            <div class="cbh-native-finder__result-actions">
              <button class="cbh-native-finder__button cbh-native-finder__button--solid" type="button" data-cbh-change-answers="${exactType ? 'cbdType' : 'format'}">Change Preferences</button>
              <a class="cbh-native-finder__button cbh-native-finder__button--soft" href="${escapeHtml(safeUrl(route.url))}">Browse All Products</a>
            </div>
          </div>
        </section>`;
    }

    loadErrorMarkup(route, error) {
      return `
        <section class="cbh-native-finder__matches" data-cbh-feed-error="${escapeHtml(error?.code || 'unknown')}">
          <div class="cbh-native-finder__load-error">
            <span class="cbh-native-finder__catalogue-error-icon" aria-hidden="true">${iconMarkup('question')}</span>
            <h3>We Could Not Load Your Products</h3>
            <p>Try the product check again or browse the full matching range to see current prices, variants and availability.</p>
            <div class="cbh-native-finder__result-actions">
              <button class="cbh-native-finder__button cbh-native-finder__button--solid" type="button" data-cbh-retry-results>Try Again</button>
              <a class="cbh-native-finder__button cbh-native-finder__button--soft" href="${escapeHtml(safeUrl(route.url))}">Browse Matching Range</a>
            </div>
          </div>
        </section>`;
    }

    renderTypeEducationRoute() {
      const compareUrl = safeUrl(this.config.guides?.compareTypes, safeUrl(this.config.guides?.allCbd, '/collections/all-cbd-products'));
      const guideAvailable = this.isExpectedGuide(compareUrl, '/pages/compare-cbd-types');
      const heading = guideAvailable ? 'Compare CBD Types Before Products' : 'Browse Current CBD Products';
      const intro = guideAvailable
        ? 'The comparison guide explains Full Spectrum, Broad Spectrum and CBD Isolate equally, including the product details and THC wording to check.'
        : 'Browse the current CBD range and open product pages to compare the stated CBD type, format, strength and label information without assuming a profile.';
      const actionLabel = guideAvailable ? 'Open CBD Type Guide' : 'Browse All CBD Products';
      this.root.classList.add('has-results');
      this.form.hidden = true;
      this.results.hidden = false;
      this.renderProgress(4, true);
      this.results.innerHTML = `
        <section class="cbh-native-finder__result-route cbh-native-finder__result-route--education" aria-labelledby="${escapeHtml(this.config.sectionId)}-result-heading">
          <div>
            <p class="cbh-native-finder__result-kicker">Your next step</p>
            <h2 id="${escapeHtml(this.config.sectionId)}-result-heading" tabindex="-1">${escapeHtml(heading)}</h2>
            <p class="cbh-native-finder__result-intro">${escapeHtml(intro)}</p>
            <div class="cbh-native-finder__result-actions">
              <a class="cbh-native-finder__result-action cbh-native-finder__result-action--primary" href="${escapeHtml(compareUrl)}">${escapeHtml(actionLabel)} <span aria-hidden="true">→</span></a>
              <button class="cbh-native-finder__result-action cbh-native-finder__result-action--secondary" type="button" data-cbh-change-answers="approach">Choose A Product Format Instead</button>
            </div>
          </div>
          <div class="cbh-native-finder__result-reasons">
            <h3>${guideAvailable ? 'The guide compares' : 'What to compare'}</h3>
            <ul><li>Full Spectrum products</li><li>Broad Spectrum products</li><li>CBD Isolate products</li><li>CBD type separately from strength</li></ul>
          </div>
        </section>`;
      this.results.querySelector('h2')?.focus({ preventScroll: true });
      this.trackResultView(guideAvailable ? 'cbd_type_guide' : 'all_cbd_fallback');
      if (this.live) this.live.textContent = guideAvailable
        ? 'The CBD type comparison guide is your suggested next step.'
        : 'The current CBD range is ready to browse without a selected CBD type.';
    }

    renderEducationRoute() {
      const formatUrl = safeUrl(this.config.guides?.format, safeUrl(this.config.guides?.allCbd, '/collections/all-cbd-products'));
      const guideAvailable = this.isExpectedGuide(formatUrl, '/pages/shop-by-format');
      const heading = guideAvailable ? 'Compare CBD Formats First' : 'Browse CBD Products By Format';
      const intro = guideAvailable
        ? 'The format guide explains oils, gummies, capsules, topicals, drinks, patches, vape products and extracts before you enter a collection.'
        : 'Use the current CBD range and its format filters to compare oils, gummies, capsules, topicals, drinks, patches, vape products and extracts.';
      const actionLabel = guideAvailable ? 'Open Shop By Format' : 'Browse All CBD Products';
      this.root.classList.add('has-results');
      this.form.hidden = true;
      this.results.hidden = false;
      this.renderProgress(4, true);
      this.results.innerHTML = `
        <section class="cbh-native-finder__result-route cbh-native-finder__result-route--education" aria-labelledby="${escapeHtml(this.config.sectionId)}-result-heading">
          <div>
            <p class="cbh-native-finder__result-kicker">Your next step</p>
            <h2 id="${escapeHtml(this.config.sectionId)}-result-heading" tabindex="-1">${escapeHtml(heading)}</h2>
            <p class="cbh-native-finder__result-intro">${escapeHtml(intro)}</p>
            <div class="cbh-native-finder__result-actions">
              <a class="cbh-native-finder__result-action cbh-native-finder__result-action--primary" href="${escapeHtml(formatUrl)}">${escapeHtml(actionLabel)} <span aria-hidden="true">→</span></a>
              <button class="cbh-native-finder__result-action cbh-native-finder__result-action--secondary" type="button" data-cbh-change-answers="format">Choose A Format Here</button>
            </div>
          </div>
          <div class="cbh-native-finder__result-reasons">
            <h3>Why products are not shown yet</h3>
            <ul><li>You chose to keep comparing formats.</li><li>No product format has been assumed.</li><li>You can return whenever you are ready.</li></ul>
          </div>
        </section>`;
      this.results.querySelector('h2')?.focus({ preventScroll: true });
      this.trackResultView(guideAvailable ? 'format_guide' : 'all_cbd_fallback');
      if (this.live) this.live.textContent = guideAvailable
        ? 'The format comparison guide is your suggested next step.'
        : 'The current CBD range is ready to browse by format.';
    }

    renderAgeStop() {
      this.root.classList.add('has-results');
      this.form.hidden = true;
      this.results.hidden = false;
      this.renderProgress(4, true);
      this.results.innerHTML = `
        <div class="cbh-native-finder__age-stop">
          <span class="cbh-native-finder__catalogue-error-icon" aria-hidden="true">${iconMarkup('adult')}</span>
          <p class="cbh-native-finder__result-kicker">CBD vape products</p>
          <h2 tabindex="-1">The CBD Vape Finder Is For Adults Aged 18+</h2>
          <p>Choose another CBD format to continue browsing products.</p>
          <button class="cbh-native-finder__button cbh-native-finder__button--solid" type="button" data-cbh-change-answers="format">Choose Another Format</button>
        </div>`;
      this.results.querySelector('h2')?.focus({ preventScroll: true });
      this.trackResultView('adult_route_stop');
      if (this.live) this.live.textContent = 'The adult CBD vape route is not available.';
    }

    returnToAnswers(key) {
      if (key === 'approach') {
        this.answers.approach = '';
        this.clearAnswers(['format', 'routePreference', 'vapeSubtype', 'age', 'cbdType', 'flavour', 'shopping']);
        this.currentFeed = null;
      }
      const steps = this.activeSteps();
      const targetIndex = steps.findIndex((step) => step.key === key);
      this.stepIndex = targetIndex >= 0 ? targetIndex : Math.min(1, steps.length - 1);
      this.returnToForm();
    }

    returnToForm() {
      this.root.classList.remove('has-results');
      this.results.hidden = true;
      this.results.innerHTML = '';
      this.form.hidden = false;
      this.controls.hidden = false;
      this.renderStep(true);
      this.tool?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.cbhFinderInitialised === 'true') return;
      root.dataset.cbhFinderInitialised = 'true';
      new CBHNativeFinder(root).init();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
